import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Folder, FolderPlus, ChevronDown, Check, Plus, X, Save, Edit3 } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { getUser, getAuthToken, successMessage, errorMessage } from '../utils/helpers';
import { NODE_BACKEND_URL } from '../utils/constant';

const FolderDropdown = ({
  selectedFolderId,
  onFolderSelect,
  label = "Select Folder",
  className = "",
  compact = false
}) => {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const [isOpen, setIsOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Folder name validation function
  const validateFolderName = (name) => {
    const trimmedName = name.trim();
    
    // Check if empty
    if (!trimmedName) {
      return { isValid: false, error: 'Folder name cannot be empty' };
    }

    // Check length (typically 1-255 characters)
    if (trimmedName.length > 255) {
      return { isValid: false, error: 'Folder name is too long (maximum 255 characters)' };
    }

    // Invalid characters for most file systems: \ / : * ? " < > |
    const invalidChars = /[\\/:*?"<>|]/;
    if (invalidChars.test(trimmedName)) {
      return { isValid: false, error: 'Folder name contains invalid characters (\\/:*?"<>|)' };
    }

    // Check for reserved names (Windows)
    const reservedNames = [
      'CON', 'PRN', 'AUX', 'NUL',
      'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
      'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9'
    ];
    
    const upperName = trimmedName.toUpperCase();
    const nameWithoutExtension = upperName.split('.')[0];
    
    if (reservedNames.includes(upperName) || reservedNames.includes(nameWithoutExtension)) {
      return { isValid: false, error: 'Folder name is a reserved system name' };
    }

    // Check for names that are just dots
    if (/^\.+$/.test(trimmedName)) {
      return { isValid: false, error: 'Folder name cannot consist only of dots' };
    }

    // Check for trailing dots or spaces (problematic on Windows)
    if (trimmedName.endsWith('.') || trimmedName.endsWith(' ')) {
      return { isValid: false, error: 'Folder name cannot end with a dot or space' };
    }

    // Check for leading/trailing whitespace (we already trim, but double-check)
    if (name !== trimmedName) {
      return { isValid: false, error: 'Folder name cannot start or end with whitespace' };
    }

    // Check for duplicate names (case-insensitive)
    const isDuplicate = folders.some(folder => 
      folder.name.toLowerCase() === trimmedName.toLowerCase() && 
      folder.id !== editingFolderId // Allow editing current folder to same name
    );
    
    if (isDuplicate) {
      return { isValid: false, error: 'A folder with this name already exists' };
    }

    return { isValid: true, error: null };
  };

  // Fetch folders when component mounts
  useEffect(() => {
    fetchFolders();
  }, []);

  // Update selected folder when selectedFolderId changes
  useEffect(() => {
    if (selectedFolderId && folders.length > 0) {
      const folder = folders.find(f => f.id === selectedFolderId);
      if (folder) {
        setSelectedFolder(folder);
      }
    } else {
      setSelectedFolder(null);
    }
  }, [selectedFolderId, folders]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsCreatingFolder(false);
        setEditingFolderId(null);
        setEditFolderName('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchFolders = async () => {
    setIsLoading(true);
    try {
      // Use axios directly with NODE_BACKEND_URL (not apiClient which uses gateway)
      const response = await axios.get(`${NODE_BACKEND_URL}/api/get-folders?user_id=${getUser()?.id}`, {
        headers: { Authorization: getAuthToken() }
      });
      // Assuming the API returns an array of folder objects with id and name
      setFolders(response.data?.data);
    } catch (error) {
      console.error('Error fetching folders:', error);
      // If the API fails, provide some default folders
      setFolders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    const validation = validateFolderName(newFolderName);
    
    if (!validation.isValid) {
      errorMessage(validation.error);
      return;
    }

    const trimmedName = newFolderName.trim();
    setIsLoading(true);
    
    try {
      // Use axios directly with NODE_BACKEND_URL (not apiClient which uses gateway)
      const response = await axios.post(`${NODE_BACKEND_URL}/api/create-folder`, {
        name: trimmedName,
        user_id: getUser()?.id
      }, {
        headers: { Authorization: getAuthToken() }
      });

      // Add the new folder to the list
      const newFolder = response.data?.data;
      setFolders(prev => [...prev, newFolder]);

      // Update the selected folder in state
      setSelectedFolder(newFolder);
      
      // Auto-select the newly created folder by calling the onFolderSelect callback
      if (onFolderSelect && newFolder?.id) {
        onFolderSelect(newFolder.id);
      }

      // Reset state
      setNewFolderName('');
      setIsCreatingFolder(false);
      successMessage(`Folder "${trimmedName}" created successfully`);
    } catch (error) {
      console.error('Error creating folder:', error);
      // If the API fails, create a mock folder for demo purposes
      const mockFolder = { id: `folder-${Date.now()}`, name: trimmedName };
      setFolders(prev => [...prev, mockFolder]);
      setSelectedFolder(mockFolder);
      
      // Auto-select the mock folder as well
      if (onFolderSelect && mockFolder?.id) {
        onFolderSelect(mockFolder.id);
      }
      
      setNewFolderName('');
      setIsCreatingFolder(false);
      successMessage(`Folder "${trimmedName}" created successfully`);
    } finally {
      setIsLoading(false);
      setIsOpen(false); // Close the dropdown after creating folder
    }
  };

  const handleEditFolder = async (folderId) => {
    const validation = validateFolderName(editFolderName);
    
    if (!validation.isValid) {
      errorMessage(validation.error);
      return;
    }

    const trimmedName = editFolderName.trim();
    setIsLoading(true);
    
    try {
      // Use axios directly with NODE_BACKEND_URL (not apiClient which uses gateway)
      const response = await axios.put(`${NODE_BACKEND_URL}/api/update-folder/${folderId}`, {
        name: trimmedName,
        user_id: getUser()?.id
      }, {
        headers: { Authorization: getAuthToken() }
      });

      // Update the folder in the list
      const updatedFolder = response.data?.data;
      setFolders(prev => prev.map(folder => 
        folder.id === folderId 
          ? { ...folder, name: trimmedName }
          : folder
      ));

      // Update selected folder if it's the one being edited
      if (selectedFolder && selectedFolder.id === folderId) {
        setSelectedFolder({ ...selectedFolder, name: trimmedName });
      }

      // Reset editing state
      setEditingFolderId(null);
      setEditFolderName('');
      successMessage(`Folder renamed to "${trimmedName}" successfully`);
    } catch (error) {
      console.error('Error updating folder:', error);
      // If the API fails, update locally for demo purposes
      setFolders(prev => prev.map(folder => 
        folder.id === folderId 
          ? { ...folder, name: trimmedName }
          : folder
      ));

      // Update selected folder if it's the one being edited
      if (selectedFolder && selectedFolder.id === folderId) {
        setSelectedFolder({ ...selectedFolder, name: trimmedName });
      }

      setEditingFolderId(null);
      setEditFolderName('');
      successMessage(`Folder renamed to "${trimmedName}" successfully`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
    if (isCreatingFolder) {
      setIsCreatingFolder(false);
    }
    if (editingFolderId) {
      setEditingFolderId(null);
      setEditFolderName('');
    }
  };

  const selectFolder = (folder) => {
    if (onFolderSelect) {
      onFolderSelect(folder.id);
    }
    setSelectedFolder(folder);
    setIsOpen(false);
  };

  const startCreatingFolder = () => {
    setIsCreatingFolder(true);
    setEditingFolderId(null);
    setEditFolderName('');
  };

  const cancelCreateFolder = () => {
    setIsCreatingFolder(false);
    setNewFolderName('');
  };

  const startEditingFolder = (folder, event) => {
    event.stopPropagation(); // Prevent folder selection when clicking edit
    setEditingFolderId(folder.id);
    setEditFolderName(folder.name);
    setIsCreatingFolder(false);
  };

  const cancelEditFolder = () => {
    setEditingFolderId(null);
    setEditFolderName('');
  };

  // Helper function to check if save button should be disabled
  const isSaveDisabled = (name) => {
    const validation = validateFolderName(name);
    return !validation.isValid || isLoading;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Label - hidden in compact mode */}
      {label && !compact && (
        <label className={`block text-sm font-medium mb-1 ${'text-gray-300'}`}>
          {label}
        </label>
      )}

      {/* Dropdown Button */}
      <button
        onClick={toggleDropdown}
        className={`w-full flex items-center justify-between ${compact ? 'px-3 py-1.5' : 'px-4 py-2'} border rounded-${compact ? 'lg' : 'md'} ${isOpen
          ? `${'border-blue-500 bg-blue-900/20'}`
          : `${'border-white/10 bg-transparent'}`
          } transition-colors`}
        style={compact ? { background: 'rgba(255, 255, 255, 0.03)' } : {}}
        disabled={isLoading}
      >
        <div className="flex items-center">
          <Folder size={compact ? 14 : 18} className={`mr-2 ${'text-gray-300'}`} />
          <span className={`truncate ${compact ? 'text-sm' : ''} ${'text-white'}`}>
            {selectedFolder ? selectedFolder.name : (compact ? 'Folder' : 'Select Folder')}
          </span>
        </div>
        <ChevronDown size={compact ? 14 : 18} className={`transform transition-transform ${isOpen ? 'rotate-180' : ''} ${'text-gray-400'}`} />
      </button>

      {/* Dropdown Menu - high z-index to appear above all other content */}
      {isOpen && (
        <div className={`absolute z-[9999] mt-1 w-full rounded-md shadow-lg ${'bg-gray-800 border border-gray-600'}`}
             style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <div className="py-1 w-full">
            {/* Create New Folder Option - Now at the top */}
            {!isCreatingFolder ? (
              <button
                onClick={startCreatingFolder}
                className={`w-full text-left px-4 py-2 flex items-center border-b ${'text-green-400 hover:bg-gray-700 border-gray-600'
                  }`}
              >
                <FolderPlus size={16} className="mr-2" />
                <span>Create New Folder</span>
              </button>
            ) : (
              <div className={`p-2 border-b ${'border-gray-600'}`}>
                <div className="flex items-center">
                  <FolderPlus size={16} className={`mr-2 ${'text-green-400'}`} />
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Folder name"
                    className={`flex-1 px-2 py-1 text-sm border rounded-md ${'bg-gray-700 border-gray-600 text-white'
                      } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateFolder();
                      } else if (e.key === 'Escape') {
                        cancelCreateFolder();
                      }
                    }}
                  />
                  <button
                    onClick={handleCreateFolder}
                    disabled={isSaveDisabled(newFolderName)}
                    className={`ml-2 p-1 rounded-md ${'bg-green-700 text-white hover:bg-green-600'
                      } disabled:opacity-50`}
                    title="Save folder"
                  >
                    <Save size={16} />
                  </button>
                  <button
                    onClick={cancelCreateFolder}
                    className={`ml-1 p-1 rounded-md ${'bg-gray-600 text-white hover:bg-gray-500'
                      }`}
                    title="Cancel"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}

            {folders.map((folder) => (
              <div key={folder.id}>
                {editingFolderId === folder.id ? (
                  // Edit mode for this folder
                  <div className={`px-2 py-2 border-b ${'border-gray-600'}`}>
                    <div className="flex items-center">
                      <Folder size={16} className={`mr-2 ${'text-blue-400'}`} />
                      <input
                        type="text"
                        value={editFolderName}
                        onChange={(e) => setEditFolderName(e.target.value)}
                        className={`flex-1 px-2 py-1 text-sm border rounded-md ${'bg-gray-700 border-gray-600 text-white'
                          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleEditFolder(folder.id);
                          } else if (e.key === 'Escape') {
                            cancelEditFolder();
                          }
                        }}
                      />
                      <button
                        onClick={() => handleEditFolder(folder.id)}
                        disabled={isSaveDisabled(editFolderName)}
                        className={`ml-2 p-1 rounded-md ${'bg-blue-700 text-white hover:bg-blue-600'
                          } disabled:opacity-50`}
                        title="Save changes"
                      >
                        <Save size={16} />
                      </button>
                      <button
                        onClick={cancelEditFolder}
                        className={`ml-1 p-1 rounded-md ${'bg-gray-600 text-white hover:bg-gray-500'
                          }`}
                        title="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  // Normal folder display
                  <button
                    onClick={() => selectFolder(folder)}
                    className={`w-full text-left px-4 py-2 flex items-center group ${selectedFolder && selectedFolder.id === folder.id
                      ? 'bg-blue-900/30 text-blue-400'
                      : 'text-gray-200 hover:bg-gray-700'
                      }`}
                  >
                    <Folder size={16} className="mr-2" />
                    <span className="flex-1 truncate">{folder.name}</span>
                    <div className="flex items-center">
                      {selectedFolder && selectedFolder.id === folder.id && (
                        <Check size={16} className={`mr-2 ${'text-blue-400'}`} />
                      )}
                      <button
                        onClick={(e) => startEditingFolder(folder, e)}
                        className={`p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity ${'hover:bg-gray-600 text-gray-400 hover:text-white'
                          }`}
                        title="Edit folder name"
                      >
                        <Edit3 size={14} />
                      </button>
                    </div>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderDropdown;